import PropTypes from "prop-types";
import React, {
  useState,
  useContext,
  useCallback,
  useRef,
  useEffect,
} from "react";
import _ from "lodash";
import { JssProvider, createUseStyles } from "react-jss";
import { SearchFormNavBar } from "./widgets";
import { StyleTypeContext, TranslationContext } from "../../umbilical_tools";
import "react-datepicker/dist/react-datepicker.css";
import "react-datepicker/dist/react-datepicker-cssmodules.css";
import { NoScrollContext } from "../../context";
import { useDispatch, useSelector } from "react-redux";
import { tourSearchFormChange } from "../../actions/search";
import { getProductCategoriesSelector } from "../../umbilical_hocs";
import { withRouter } from "react-router-dom";
import { PackageSearchForm } from "./package";
import { MiSearchForm } from "./mi";
import { flightSearchFormUpdate } from "../../actions/search/flights_search";
import RestaurantsSearchForm from "./restaurant";
import { activitySearchFormUpdate } from "../../actions/search/activities_search";
import { transferSearchFormUpdate } from "../../actions/search/transfer_search";
import { SearchButton } from "../common/Button";
import tinycolor from "tinycolor2";
import { HotelSearchForm } from "./hotel";
import { TransferSearchForm } from "./transfer";
import { ActivitySearchForm } from "./activity";
import jssVariables from "../../jss_setup";
import FlightsSearchForm from "./flights";
import { CarRentalSearchForm } from "./carRental";
import { TrainSearchForm } from "./train";
import { FerrySearchForm } from "./ferry";
import { ferrySearchFormUpdate } from "../../actions/search/ferries_search";
import PaxPickerModalACC from "./modals/PaxPickerModalACC";
import PaxPickerModal from "./modals/PaxPickerModal";
import RailPaxPickerModal from "./modals/RailPaxPickerModal";

function getSearchForm({ state, product_type }) {
  switch (product_type) {
    case "MI":
    case "PKG":
      return state.TourSearchForm;
    case "FL":
      return state.FlightSearchForm;
    case "ACT":
      return state.ActivitySearchForm;
    case "REST":
      return state.RestaurantSearchForm;
    case "TRF":
      return state.TransfersSearchForm;
    case "CAR":
      return state.CarRentalSearchForm;
    case "TRA":
      return state.TrainSearchForm;
    case "FER":
      return state.FerrySearchForm;
    default:
      break;
  }
}

const mainSearchFormStyles = createUseStyles({
  mainSearchForm: (props) => ({
    position: "absolute",
    width: `calc(100% - 10vw)`,
    borderRadius: `calc(${jssVariables.border_radius} * 1.5)`,
    transform: "translateX(-50%)",
    left: "50%",
    padding: jssVariables.normal_gap,
    bottom: "4rem",
    height: "max-content",
    background: tinycolor("white").setAlpha(0.6).toRgbString(),
    backdropFilter: "blur(15px)",
    zIndex: 1,
    ...(props?.customStyles?.mainSearchForm ?? {}),
  }),
  searchBtnContr: (props) => ({
    width: "100%",
    height: "max-content",
    display: "grid",
    justifyItems: "center",
    justifyContent: "center",
    paddingTop: jssVariables.normal_gap,
    ...(props?.customStyles?.searchBtnContr ?? {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    mainSearchForm: (props) => ({
      minWidth: "65rem",
      width: "max-content",
      maxWidth: "70rem",
      bottom: "4rem",
      marginBottom: props.withNav
        ? "unset"
        : `calc(${jssVariables.normal_gap} * 2.5)`,
      padding: jssVariables.double_gap,
      zIndex: 1082,
      ...(props?.customStyles?.mainSearchForm?.smallscreen ?? {}),
    }),
    searchBtnContr: (props) => ({
      padding: 0,
      justifyContent: "end",
      ...(props?.customStyles?.searchBtnContr?.smallscreen ?? {}),
    }),
  },
  [`@media ${jssVariables.media.bigscreen}`]: {
    mainSearchForm: (props) => ({
      maxWidth: "90rem",
      minWidth: "70rem",
      ...(props?.customStyles?.mainSearchForm?.bigscreen ?? {}),
    }),
  },
});
const MainSearchForm = ({
  withNav,
  product_type_fixed,
  hasSingleProduct,
  setMainSearchFormPosition,
  setShowHeaderMobile,
}) => {
  const { type, brand, cStyler } = useContext(StyleTypeContext);
  const { t } = useContext(TranslationContext);
  const msfRef = useRef(null);

  const { form_types, custom_form_types } = useSelector((state) => {
    const form_types = getProductCategoriesSelector(state);
    const custom_form_types =
      state.WebsiteMetadata?.metadata?.custom_search_form_tabs ?? [];
    return { form_types, custom_form_types };
  });
  const [product_type, setProductType] = useState(
    product_type_fixed
      ? product_type_fixed
      : brand === "atlantic"
      ? "FL"
      : _.sortBy(form_types)?.[0] ?? ""
  );

  const { max_rooms_allowed, search_form, has_search_form } = useSelector(
    (state) => {
      return {
        max_rooms_allowed:
          state.WebsiteMetadata?.metadata?.max_rooms_allowed ?? 3,
        search_form: getSearchForm({ state, product_type }),
        has_search_form:
          state.WebsiteMetadata?.metadata?.landing_page?.has_search_form ??
          false,
      };
    }
  );

  const dispatch = useDispatch();

  const onFormFlightUpdate = useCallback(
    ({ form }) => dispatch(flightSearchFormUpdate({ form })),
    [dispatch]
  );

  const onFormActivityUpdate = useCallback(
    ({ form }) => dispatch(activitySearchFormUpdate({ form })),
    [dispatch]
  );

  const onFormTransferUpdate = useCallback(
    ({ form }) => dispatch(transferSearchFormUpdate({ form })),
    [dispatch]
  );

  const onFormFerryUpdate = useCallback(
    ({ form }) => dispatch(ferrySearchFormUpdate({ form })),
    [dispatch]
  );

  const onChange = useCallback(
    (key, value) => {
      dispatch(tourSearchFormChange(key, value));
    },
    [dispatch]
  );

  const [showPaxPickerModal, setShowPaxPickerModal] = useState(false);
  const [showRailPaxPickerModal, setShowRailPaxPickerModal] = useState(false);
  const [loadingSearchBtn, setLoadingSearchBtn] = useState(false);

  const { toggleNoScroll } = useContext(NoScrollContext);

  const customStyles = cStyler({
    type,
    brand,
    objName: "lpMainSearchFormStyles",
  });

  const classes = mainSearchFormStyles({
    customStyles,
    first_active: product_type === form_types?.[0] ?? "",
    product_type,
    withNav,
  });

  const commonProps = {
    max_rooms_allowed,
    setLoadingSearchBtn,
    setShowPaxPickerModal,
    setShowRailPaxPickerModal,
    customStyles,
  };

  const searchBtnProps = {
    text: t("search"),
    loading: loadingSearchBtn,
    customStyles: customStyles?.searchBtn ?? {},
  };

  // Mobile view adjustments
  useEffect(() => {
    const noHeaderProductsMobile = ["CAR"];

    if (noHeaderProductsMobile.includes(product_type)) {
      setShowHeaderMobile(false);
    } else {
      setShowHeaderMobile(true);
    }
    const element = msfRef.current;

    if (element) {
      const elementPosition = element.getBoundingClientRect();
      const { top } = elementPosition;

      setMainSearchFormPosition(top);
    }
  }, [product_type]);

  return (
    !!has_search_form && (
      <React.Fragment>
        <div className={classes.mainSearchForm} ref={msfRef}>
          {withNav && (
            <SearchFormNavBar
              customStyles={customStyles}
              product_type={product_type}
              form_types={form_types}
              custom_form_types={custom_form_types}
              setProductType={setProductType}
              hasSingleProduct={hasSingleProduct}
            />
          )}
          {product_type === "ACC" && (
            <JssProvider classNamePrefix="ACC--">
              <HotelSearchForm {...commonProps} />
            </JssProvider>
          )}
          {product_type === "FL" && (
            <JssProvider classNamePrefix="FL--">
              <FlightsSearchForm {...commonProps} />
            </JssProvider>
          )}
          {product_type === "TRF" && (
            <JssProvider classNamePrefix="TRF--">
              <TransferSearchForm {...commonProps} />
            </JssProvider>
          )}
          {product_type === "CAR" && (
            <JssProvider classNamePrefix="CAR--">
              <CarRentalSearchForm {...commonProps} />
            </JssProvider>
          )}
          {product_type === "ACT" && (
            <JssProvider classNamePrefix="ACT--">
              <ActivitySearchForm {...commonProps} />
            </JssProvider>
          )}
          {product_type === "dream_packages" && (
            <JssProvider classNamePrefix="MI--">
              <MiSearchForm {...commonProps} theme="dream_packages">
                <div className={classes.searchBtnContr}>
                  <SearchButton {...searchBtnProps} />
                </div>
              </MiSearchForm>
            </JssProvider>
          )}
          {product_type === "PKG" && (
            <JssProvider classNamePrefix="PKG--">
              <PackageSearchForm {...commonProps} onChange={onChange} />
            </JssProvider>
          )}
          {product_type === "MI" && (
            <JssProvider classNamePrefix="MI--">
              <MiSearchForm {...commonProps}>
                <div className={classes.searchBtnContr}>
                  <SearchButton {...searchBtnProps} />
                </div>
              </MiSearchForm>
            </JssProvider>
          )}
          {product_type === "things_to_do" && (
            <JssProvider classNamePrefix="MI--">
              <MiSearchForm {...commonProps} theme="things_to_do">
                <div className={classes.searchBtnContr}>
                  <SearchButton {...searchBtnProps} />
                </div>
              </MiSearchForm>
            </JssProvider>
          )}
          {product_type === "partying" && (
            <JssProvider classNamePrefix="MI--">
              <MiSearchForm {...commonProps} theme="partying">
                <div className={classes.searchBtnContr}>
                  <SearchButton {...searchBtnProps} />
                </div>
              </MiSearchForm>
            </JssProvider>
          )}
          {product_type === "REST" && (
            <JssProvider classNamePrefix="REST--">
              <RestaurantsSearchForm {...commonProps} />
            </JssProvider>
          )}
          {product_type === "TRA" && (
            <JssProvider classNamePrefix="TRA--">
              <TrainSearchForm {...commonProps} />
            </JssProvider>
          )}
          {product_type === "FER" && (
            <JssProvider classNamePrefix="FER--">
              <FerrySearchForm {...commonProps} />
            </JssProvider>
          )}
        </div>
        {showPaxPickerModal && (
          <React.Fragment>
            {product_type === "ACC" ? (
              <PaxPickerModalACC
                onCancel={() => {
                  toggleNoScroll();
                  setShowPaxPickerModal(false);
                }}
              />
            ) : (
              <PaxPickerModal
                adults={search_form.adults}
                childs={search_form.children}
                children_ages={search_form.children_ages}
                infantsOnLap={0}
                infantsInSeat={0}
                onFormUpdate={({ form }) => {
                  if (product_type === "FL") {
                    onFormFlightUpdate({ form });
                  } else if (product_type === "ACT") {
                    onFormActivityUpdate({ form });
                  } else if (product_type === "TRF") {
                    delete form.infantsInSeat;
                    delete form.infantsOnLap;
                    onFormTransferUpdate({ form });
                  } else if (product_type === "FER") {
                    onFormFerryUpdate({ form });
                  }
                }}
                onCancel={() => {
                  toggleNoScroll(false);
                  setShowPaxPickerModal(false);
                }}
                product_type={product_type}
              />
            )}
          </React.Fragment>
        )}
        {showRailPaxPickerModal && (
          <RailPaxPickerModal
            onCancel={() => {
              toggleNoScroll();
              setShowRailPaxPickerModal(false);
            }}
          />
        )}
      </React.Fragment>
    )
  );
};
MainSearchForm.defaultProps = {
  withNav: true,
  product_type_fixed: "",
  hasSingleProduct: false,
  setMainSearchFormPosition: () => {},
  setShowHeaderMobile: () => {},
};
MainSearchForm.propTypes = {
  withNav: PropTypes.bool,
  history: PropTypes.object.isRequired,
  product_type_fixed: PropTypes.string,
  hasSingleProduct: PropTypes.bool,
  msfRef: PropTypes.object,
  setMainSearchFormPosition: PropTypes.func,
  setShowHeaderMobile: PropTypes.func,
};
export default withRouter(MainSearchForm);
